import React from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { resetPasswordSchema, type ResetPasswordFormData } from './validationSchema';
import AlertMessage from '../../shared/components/ui/AlertMessage';
import PublicNavigation from '../../shared/components/layout/PublicNavigation';
import Footer from '../../shared/components/layout/Footer';

const ResetPasswordPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [error, setError] = React.useState<string | null>(null);
  const [isSuccess, setIsSuccess] = React.useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting }
  } = useForm<ResetPasswordFormData>({
    resolver: zodResolver(resetPasswordSchema)
  });

  React.useEffect(() => {
    if (!token) {
      setError('Invalid or expired reset token');
    }
  }, [token]);

  const onSubmit = async (data: ResetPasswordFormData) => {
    try {
      setError(null);
      // TODO: Implement actual password reset logic
      console.log('Reset password with:', { ...data, token });
      
      // Simulate an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // For demonstration, let's simulate an error sometimes
      if (data.password === 'error123!A') {
        throw new Error('This password has been compromised in a data breach');
      }
      
      setIsSuccess(true);
      setTimeout(() => navigate('/login'), 3000);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred');
    }
  };

  if (isSuccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col">
        <PublicNavigation />
        <div className="flex-grow flex items-center justify-center py-12 sm:px-6 lg:px-8">
          <div className="sm:mx-auto sm:w-full sm:max-w-md">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
              <div className="text-center">
                <AlertMessage
                  type="success"
                  message="Your password has been reset successfully. You will be redirected to the login page."
                />
                <div className="mt-4">
                  <Link
                    to="/login"
                    className="text-green-600 hover:text-green-500 font-medium"
                  >
                    Go to login
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <PublicNavigation />
      <div className="flex-grow flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="w-full max-w-md mx-auto">
          {/* Logo and heading section - full width on all screens */}
          <div className="w-full mb-8">
            <div className="flex justify-center mb-6">
              <Link to="/">
                <img
                  src="/Vester-logo-lrg-clr.png"
                  alt="Vester"
                  className="h-20 sm:h-24 w-auto"
                />
              </Link>
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Set new password
            </h2>
          </div>

          {/* Form section - full width on all screens */}
          <div className="w-full">
            <div className="bg-white py-8 px-4 shadow sm:rounded-lg sm:px-10">
          {error && (
            <div className="mb-4">
              <AlertMessage
                type="error"
                message={error}
                onClose={() => setError(null)}
              />
            </div>
          )}

          <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                New password
              </label>
              <div className="mt-1">
                <input
                  id="password"
                  {...register('password')}
                  type="password"
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password.message}</p>
                )}
              </div>
              <div className="mt-2">
                <ul className="text-xs text-gray-500 space-y-1">
                  <li className="flex items-center">
                    <span className={`mr-2 ${errors.password ? 'text-red-500' : 'text-green-500'}`}>•</span>
                    At least 8 characters
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 ${errors.password ? 'text-red-500' : 'text-green-500'}`}>•</span>
                    One uppercase letter
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 ${errors.password ? 'text-red-500' : 'text-green-500'}`}>•</span>
                    One lowercase letter
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 ${errors.password ? 'text-red-500' : 'text-green-500'}`}>•</span>
                    One number
                  </li>
                  <li className="flex items-center">
                    <span className={`mr-2 ${errors.password ? 'text-red-500' : 'text-green-500'}`}>•</span>
                    One special character
                  </li>
                </ul>
              </div>
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                Confirm new password
              </label>
              <div className="mt-1">
                <input
                  id="confirmPassword"
                  {...register('confirmPassword')}
                  type="password"
                  autoComplete="new-password"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-green-500 focus:border-green-500 sm:text-sm"
                />
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword.message}</p>
                )}
              </div>
            </div>

            <div>
              <button
                type="submit"
                disabled={isSubmitting || !token}
                className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isSubmitting ? 'Resetting password...' : 'Reset password'}
              </button>
            </div>
          </form>
            </div>
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default ResetPasswordPage;
